import { useContext, useEffect, useState } from 'react';
import { Row } from 'react-bootstrap';
import { FacilitatorState } from '../../components/facilitator/facilitator-state';
import { GameContext } from '../../contexts/game';
import {
  getPlayers,
  getPlayerState,
  submitTaskAnswerScoringAsync
} from '../../services/players';
import { PlayerResponse } from '../../types/responses/player-response';
import { Button } from '@progress/kendo-react-buttons';

import {
  getTeamList,
  getTeamSelectedAsync,
  joinTeam,
  leaveTeam
} from '../../services/teams';
import {
  GetTeamSelectedResponse,
  TeamResponse
} from '../../types/responses/team-response';
import {
  NotificationContent,
  NotificationContext
} from '../../contexts/notification';
import { PlayerContextState } from '../../contexts/player';

import { TabButtonComponent } from '../../components/tabs/tab-button';
import { TaskContentAnswerEntity, TaskEntity } from '../../types';
import { TaskCard } from '../../components/facilitator/task/task-card';
import { UpdatePlayerStateAsync } from '../../utils/game-engine/base';
import { ManualScoring } from '../../types/manual-scoring';
import { AllPlayerFacilitatorState } from '../../features/facilitator/player/all-player-state';
import HelpSupport from '../../components/help-support';
import { GameDocumentContext } from '../../contexts/game-document';
import { TaskContentCard } from '../../components/facilitator/task/task-content-card';
import {
  GetForm,
  GetTaskContentById
} from '../../utils/game-document/assets/task-contents';
import { TaskDownloadWindow } from '../../features/facilitator/task/task-download-window';
import { FileZipDownloadRequest } from '../../types/facilitator/file-zip-download';
import { GetTaskById, GetTaskTitle } from '../../utils/game-document/assets';
import { capitalizeFirstLetter } from '../../utils/string';
import { AnswerType } from '../../types/game-document/entities/task-content';
import { TaskContentAnswer } from '../../types/entities/task';
import { GetResourceValue } from '../../utils/game-document/resources';
import { TaskState } from '../../components/facilitator/task/task-list';
import { downloadFilesAndZip } from '../../utils/file-utility';
import React from 'react';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { useDownloadSelected } from '../../hooks/use-download-selected';
import { TaskARCard } from '../../components/facilitator/task/task-AR-card';
import { TaskContentARCard } from '../../components/facilitator/task/task-content-AR-card';

export const PlayersPage = () => {
  const [game] = useContext(GameContext);
  const [gameDocument] = useContext(GameDocumentContext);
  const [notification, setNotification] = useContext(NotificationContext);
  const [players, setPlayers] = useState<PlayerResponse[]>([]);
  const [playerState, setPlayerState] = useState<PlayerContextState>();
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerResponse>();
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<GetTeamSelectedResponse>();
  const [selectedTeamCode, setSelectedTeamCode] = useState<string>();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [downloadOptionsVisible, setDownloadOptionsVisible] =
    useState<boolean>(false);

  const tabs = ['Details', 'Tasks'];
  const allPlayerCode = 'all_player';

  const taskList = (gameDocument.gameDocument?.assets.tasks ?? []).map(
    (t) => t.id
  );
  const {
    selectedDownload,
    onResetSelected,
    onDownloadSelected,
    onAddRemoveSelectedDownload
  } = useDownloadSelected(
    `${gameDocument?.gameCode!}-${selectedPlayer?.name}-download-selected.zip`
  );

  const getAllPlayers = async () => {
    let playersResult = (await getPlayers(game?.gameCode!)) as PlayerResponse[];
    playersResult = playersResult.filter((item) => item.activityTypeId === 1);

    if (playersResult.length > 0) {
      playersResult.unshift({
        activityTypeId: 0,
        name: 'All Players',
        code: allPlayerCode
      });

      setSelectedPlayer(playersResult[0]);
    }

    setPlayers(playersResult);
  };

  const getAllTeams = async () => {
    let teamResult = (await getTeamList(game?.gameCode!)) as TeamResponse[];
    setTeams(teamResult);
  };

  const onClickHandler = async (player: PlayerResponse) => {
    if (player.code !== selectedPlayer?.code) {
      setPlayerState(undefined);
      setSelectedPlayer(player);
    }
  };

  const getPlayerStateAsync = async () => {
    if (selectedPlayer && selectedPlayer.code !== allPlayerCode) {
      setSelectedTeam({});
      setSelectedTeamCode('');
      try {
        let playerStateResponse = await getPlayerState(
          game?.gameCode!,
          selectedPlayer?.code!
        );

        setPlayerState(playerStateResponse);

        if (playerStateResponse?.playerState?.teamCode) {
          let teamResult = (await getTeamSelectedAsync(
            game?.gameCode!,
            playerStateResponse?.playerState?.teamCode
          )) as GetTeamSelectedResponse;
          if (teamResult.code) {
            setSelectedTeam(teamResult);
            setSelectedTeamCode(teamResult.code!);
          } else {
            setSelectedTeam({});
            setSelectedTeamCode('');
          }
        } else {
          setSelectedTeam({});
          setSelectedTeamCode('');
        }
      } catch (error) {
        setPlayerState(undefined);
      }
    }
  };

  const handleChangeTeam = (team: TeamResponse) => {
    setSelectedTeamCode(team ? team.teamCode! : '');
  };

  const responseTeamNotification = (message: string) => {
    const responseTeamNotification: NotificationContent = {
      icon: 'notifications',
      isHide: false,
      message: (
        <span>
          <strong>{message}</strong>
        </span>
      )
    };
    const content: NotificationContent[] = notification.content;
    content.push(responseTeamNotification);
    setNotification({ ...notification, content });
  };

  const handleAddPlayerToTeam = () => {
    joinTeam(selectedTeamCode!, selectedPlayer?.code!, game?.gameCode!)
      .then((response) => {
        if (response) {
          responseTeamNotification('Success add player to a team ');
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleRemovePlayerFromTeam = () => {
    leaveTeam(selectedTeamCode!, selectedPlayer?.code!, game?.gameCode!)
      .then((response) => {
        if (response) {
          responseTeamNotification('Player has been removed from team ');
          setSelectedTeam({});
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const updatePlayerStateTaskScore = (scoring: ManualScoring) => {
    if (playerState) {
      setPlayerState((prevPlayerState) => {
        // Find the task
        const tasks = playerState?.playerState?.tasks;
        const taskIndex = tasks?.findIndex((x) => x.id === scoring.taskId);

        if (taskIndex === -1 || taskIndex === undefined) return prevPlayerState; // Task not found

        // Find the form index
        const taskContentFormAnswers =
          tasks![taskIndex]?.taskContentFormAnswers;
        const formIndex = taskContentFormAnswers?.findIndex(
          (x) => x.formId === scoring.formId
        );

        if (formIndex === -1 || formIndex === undefined) return prevPlayerState; // Form not found

        // Update the score immutably
        let newPlayerStates = { ...prevPlayerState };
        let newTasks = [...newPlayerStates.playerState!.tasks!];
        let newFormAnswers = [...newTasks[taskIndex].taskContentFormAnswers!];

        newFormAnswers[formIndex] = {
          ...newFormAnswers[formIndex],
          score: scoring.score
        };

        newTasks[taskIndex] = {
          ...newTasks[taskIndex],
          taskContentFormAnswers: newFormAnswers
        };

        newPlayerStates = {
          ...newPlayerStates,
          playerState: {
            ...newPlayerStates.playerState!,
            tasks: newTasks
          }
        };

        return UpdatePlayerStateAsync(
          prevPlayerState!,
          newPlayerStates.playerState!
        );
      });
    }
  };

  const submitScoring = async (scoring: any) => {
    try {
      await submitTaskAnswerScoringAsync(scoring);
    } catch (error) {
      console.error('Failed submit scoring');
    } finally {
      updatePlayerStateTaskScore(scoring);
    }
  };

  const reloadHandler = () => {
    try {
      setIsLoading(true);
      getPlayerStateAsync();
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const renderPlayerDetail = () => {
    if (players.length > 0 && !selectedPlayer) {
      return <h3>Please select a player</h3>;
    }
    if (players.length > 0 && selectedPlayer) {
      return (
        <FacilitatorState
          isPlayer={true}
          isGroup={false}
          playerOrTeamCode={selectedPlayer?.code!}
          teamCode={selectedTeamCode}
          state={playerState?.playerState}
          player={selectedPlayer}
          isDisableConfirm={
            game.gameState?.status === 'Finished' ||
            !game.activityState?.hasGame
          }
          nameState={selectedPlayer?.name}
          teamList={teams}
          selectedTeam={selectedTeam}
          handleChangeTeam={handleChangeTeam}
          handleAddPlayerToTeam={handleAddPlayerToTeam}
          handleRemovePlayerFromTeam={handleRemovePlayerFromTeam}
        />
      );
    }
  };

  const _exporter = React.createRef<ExcelExport>();

  const toggleDownloadOptions = () => {
    setDownloadOptionsVisible(!downloadOptionsVisible);
  };

  const exportTasks = (type: 'photos' | 'videos' | 'all') => {
    switch (type) {
      case 'photos':
        photosExport();
        break;
      case 'videos':
        videosExport();
        break;
      default:
        excelExport();
        break;
    }
    toggleDownloadOptions();
  };

  const photosExport = () => exportByType('Image', 'photos');
  const videosExport = () => exportByType('Video', 'videos');

  const excelExport = () => {
    if (_exporter.current) {
      _exporter.current.save();
    }
  };

  const exportByType = (questionType: 'Image' | 'Video', suffix: string) => {
    const taskRows = generatePlayerTaskAnswers('Task');
    const filesToDownload: FileZipDownloadRequest[] = taskRows
      .filter((task) => task.questionType === questionType && task.answer)
      .map((task) => ({
        containerFolder: '', //`${task.questionType}/`,
        fileUrl: task.answer as string
      }));

    if (filesToDownload.length > 0) {
      downloadFilesAndZip(
        filesToDownload,
        `${gameDocument?.gameCode!}-${selectedPlayer?.name}-All${suffix}.zip`
      );
    }
  };

  const generatePlayerTaskAnswers = (
    taskFilterType: 'Task' | 'OnDemandTask'
  ): TaskState[] => {
    const result: TaskState[] = [];

    playerState?.playerState?.tasks?.forEach((task) => {
      const shouldInclude =
        taskFilterType === 'Task'
          ? taskList.includes(task.id ?? '')
          : !taskList.includes(task.id ?? '');

      if (!shouldInclude) return;

      task?.taskContentFormAnswers?.forEach((taskContentAnswer) => {
        const taskById = GetTaskById(gameDocument.gameDocument, task.id || '');
        if (!taskById) return;

        const form = GetForm(
          gameDocument.gameDocument!,
          taskById.taskContentId || '',
          taskContentAnswer.formId || ''
        );
        if (!form) return;

        const question = GetResourceValue(
          gameDocument.gameDocument!,
          form.questionResId || '',
          ''
        );
        const questionType = capitalizeFirstLetter(form.type || '');
        const scoringType = form.isManualScoring ? 'M' : 'A';
        const answer = buildAnswers(form.type, taskContentAnswer) || '';

        result.push({
          taskId: task.id || '',
          taskName: GetTaskTitle(gameDocument.gameDocument, task.id || ''),
          question,
          playerName: playerState?.playerState?.name || '',
          playerCode: playerState?.playerState?.code || '',
          questionType,
          scoringType,
          formId: taskContentAnswer.formId || '',
          score: parseInt(taskContentAnswer?.score?.toString() ?? '0'),
          taskContentId: taskById.taskContentId || '',
          answer
        });
      });
    });

    return result;
  };

  const buildAnswers = (
    answerType: AnswerType,
    taskContentAnswer: TaskContentAnswer
  ) => {
    if (answerType === 'checkbox' || answerType === 'radio') {
      return taskContentAnswer?.answers
        ?.map((x) => {
          {
            return GetResourceValue(
              gameDocument?.gameDocument!,
              x.answerResId,
              ''
            );
          }
        })
        ?.join(', ');
    } else {
      return taskContentAnswer?.answers?.map((x) => x.value).join(', ');
    }
  };

  useEffect(() => {
    getAllPlayers();
    getAllTeams();
  }, []);

  useEffect(() => {
    if (selectedPlayer && selectedPlayer.code !== allPlayerCode) {
      setPlayerState(undefined);
    }
    getPlayerStateAsync();
    onResetSelected();
  }, [selectedPlayer]);

  return (
    <div
      className={
        'd-flex flex-column flex-grow-1 h-100 w-100 overflow-hidden gap-2'
      }>
      <HelpSupport
        title="Managing players"
        url="https://forum.catalystglobal.com/t/4649"
      />
      {isLoading && (
        <div
          className={
            'd-flex flex-column align-items-center justify-content-center h-100 w-100 gap-2'
          }>
          <span className={'loader'}></span>
          <label className={'text-gray'}>Loading...</label>
        </div>
      )}
      {players.length === 0 && <h3>No players found</h3>}

      <div className={'d-flex h-100 flex-grow-1 overflow-hidden'}>
        <div
          className={
            'd-flex flex-column h-100 overflow-auto group-menu border-list'
          }>
          <ul className="list-group facilitator-border-none p-2">
            {players?.map((item) => {
              return (
                <li
                  key={item.id}
                  className="list-group-item cursor-pointer"
                  onClick={() => onClickHandler(item)}>
                  <div className={'d-flex justify-content-between'}>
                    <small
                      className={`${
                        item.code === selectedPlayer?.code
                          ? 'fw-bold text-dark'
                          : 'text-dark'
                      }`}>
                      {item?.name}
                    </small>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className={'player-content'}>
          {selectedPlayer && selectedPlayer.code === allPlayerCode && (
            <Row className={'ml-3'}>
              <Row className={'container-tab-component'}>
                <TabButtonComponent
                  thisTabIndex={-1}
                  activeTab={-1}
                  title={'Details'}
                  onSetActiveTab={(tabIndex) => {}}
                />
              </Row>
              <Row className="h-100">
                <AllPlayerFacilitatorState
                  isDisableConfirm={
                    game.gameState?.status === 'Finished' ||
                    !game.activityState?.hasGame
                  }
                />
              </Row>
            </Row>
          )}

          {selectedPlayer && selectedPlayer.code !== allPlayerCode && (
            <Row className={'ml-3'}>
              <Row className={'container-tab-component'}>
                {tabs.map((item, index) => (
                  <TabButtonComponent
                    key={item}
                    thisTabIndex={index}
                    activeTab={activeTab}
                    title={item}
                    onSetActiveTab={(tabIndex) => {
                      setActiveTab(tabIndex);
                    }}
                  />
                ))}
                <div
                  onClick={reloadHandler}
                  className={
                    'd-flex justify-content-between align-items-center text-info cursor-pointer w-50'
                  }>
                  <span className="material-symbols-outlined">sync</span>
                  {activeTab === 1 && (
                    <div className={'d-flex gap-2'}>
                      <Button
                        themeColor={'success'}
                        onClick={toggleDownloadOptions}>
                        Download
                      </Button>
                      {selectedDownload.length > 0 && (
                        <Button
                          themeColor={'success'}
                          onClick={onDownloadSelected}>
                          Download Selected
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </Row>
              <hr className="mt-0" />
              <Row>
                {activeTab === 0 && renderPlayerDetail()}
                {activeTab === 1 && (
                  <Row>
                    {playerState?.playerState?.tasks?.map(
                      (task: TaskEntity, taskIndex: number) => {
                        if (task.dataARMiniGame) {
                          return taskList.includes(task.id ?? '') ? (
                            <TaskARCard
                              key={`task-ar-card-${playerState?.playerState?.code ?? selectedPlayer?.code}-${task.id}`}
                              index={taskIndex}
                              dataARMiniGame={task.dataARMiniGame}
                              selectedTask={GetTaskById(
                                gameDocument.gameDocument,
                                task.id ?? ''
                              )}
                              playerName={playerState?.playerState?.name}
                              playerCode={playerState?.playerState?.code}
                              isEdit={false}
                              onSubmitScore={submitScoring}
                            />
                          ) : (
                            <TaskContentARCard
                              key={`task-content-ar-card-${playerState?.playerState?.code ?? selectedPlayer?.code}-${task.id}`}
                              index={taskIndex}
                              dataARMiniGame={task.dataARMiniGame}
                              selectedTaskContent={GetTaskContentById(
                                gameDocument.gameDocument,
                                task.id ?? ''
                              )}
                              playerName={playerState?.playerState?.name}
                              playerCode={playerState?.playerState?.code}
                              isEdit={false}
                              onSubmitScore={submitScoring}
                            />
                          );
                        }

                        return task?.taskContentFormAnswers?.map(
                          (
                            taskContentAnswer: TaskContentAnswerEntity,
                            taskContentAnswerIndex: number
                          ) => {
                            return taskList.includes(task.id ?? '') ? (
                              <TaskCard
                                key={`task-card-${playerState?.playerState?.code ?? selectedPlayer?.code}-${task.id}-${taskContentAnswer.formId}`}
                                taskContentAnswer={taskContentAnswer!}
                                index={taskContentAnswerIndex}
                                selectedTask={GetTaskById(
                                  gameDocument.gameDocument,
                                  task.id ?? ''
                                )}
                                playerName={playerState?.playerState?.name}
                                playerCode={playerState?.playerState?.code}
                                isEdit={false}
                                onSubmitScore={submitScoring}
                                selectedDownload={selectedDownload}
                                onAddRemoveSelectedDownload={
                                  onAddRemoveSelectedDownload
                                }
                              />
                            ) : (
                              <TaskContentCard
                                key={`task-content-card-${playerState?.playerState?.code ?? selectedPlayer?.code}-${task.id}-${taskContentAnswer.formId}`}
                                taskContentAnswer={taskContentAnswer!}
                                index={taskContentAnswerIndex}
                                selectedTaskContent={GetTaskContentById(
                                  gameDocument.gameDocument,
                                  task.id ?? ''
                                )}
                                playerName={playerState?.playerState?.name}
                                playerCode={playerState?.playerState?.code}
                                isEdit={false}
                                onSubmitScore={submitScoring}
                                selectedDownload={selectedDownload}
                                onAddRemoveSelectedDownload={
                                  onAddRemoveSelectedDownload
                                }
                              />
                            );
                          }
                        );
                      }
                    )}
                  </Row>
                )}
              </Row>
            </Row>
          )}
        </div>
      </div>

      <ExcelExport
        data={generatePlayerTaskAnswers('Task')}
        collapsible={true}
        fileName={`${gameDocument?.gameCode!}-${selectedPlayer?.name}-AllTasks.xlsx`}
        ref={_exporter}>
        <ExcelExportColumn field="taskName" title="Task" width={200} />
        <ExcelExportColumn field="playerName" title="Player" width={200} />
        <ExcelExportColumn
          field="questionType"
          title="Question type"
          width={200}
        />
        <ExcelExportColumn field="scoringType" title="Type" width={100} />
        <ExcelExportColumn field="answer" title="Answer" width={500} />
        <ExcelExportColumn field="score" title="Score" width={200} />
      </ExcelExport>

      {downloadOptionsVisible && (
        <TaskDownloadWindow
          toggleDialog={toggleDownloadOptions}
          onDownload={exportTasks}
        />
      )}
    </div>
  );
};
